import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { faBell, faPen } from '@fortawesome/pro-regular-svg-icons';
import {
    faBell as solidFaBell,
    faPen as solidFaPen,
    faTrash as solidFaTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ActionMenu } from 'components/menu/ActionMenu';
import { ActionMenuButton } from 'components/menu/ActionMenuButton';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { usePopup } from 'custom_hooks/usePopup';
import { useThemeValues } from 'styles/theme';

export type SettingsActionMenuProps = {
    className?: string;
    onSettingsClick: () => void;
    onAcknowledgeAlertsClick?: () => void;
    onDeleteClick: () => void;
    canEdit?: boolean;
    canDelete?: boolean;
    renderContext?: 'table';
};
export function SettingsActionsMenu({
    className,
    onSettingsClick,
    onAcknowledgeAlertsClick,
    onDeleteClick,
    canEdit,
    canDelete,
    renderContext,
}: SettingsActionMenuProps) {
    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup();
    const { link, secondaryText, deleteButton } = useThemeValues();

    const hasMultipleActions = Boolean(onAcknowledgeAlertsClick) || Boolean(canDelete);
    const hasAnyAction = canEdit || hasMultipleActions;
    const isTableContext = renderContext === 'table';
    const showEditOnly = isTableContext && canEdit && !hasMultipleActions;

    return (
        <ActionMenuTableCell className={className}>
            {showEditOnly && (
                <HoverIconButton
                    onClick={onSettingsClick}
                    icon={faPen}
                    hoverIcon={solidFaPen}
                    color={secondaryText}
                    hoverColor={link}
                    ariaLabel="Edit Activity"
                />
            )}
            {!showEditOnly && hasAnyAction && (
                <ActionMenuButton
                    onClick={toggleOpen}
                    ref={triggerRef}
                    // the table button wrappers will handle the 'active' border styling
                    borderless={isTableContext}
                />
            )}
            {isOpen && (
                <ActionMenu
                    onClose={close}
                    ref={popupRef}
                    buttonRef={triggerRef}
                    useOverlay
                >
                    {canEdit && (
                        <ActionMenuItem
                            title={isTableContext ? 'Edit Activity' : 'Settings'}
                            hoverIcon={<FontAwesomeIcon icon={solidFaPen} color={link} />}
                            icon={<FontAwesomeIcon icon={faPen} color={secondaryText} />}
                            onSelect={() => {
                                close();
                                onSettingsClick();
                            }}
                        />
                    )}
                    {onAcknowledgeAlertsClick && (
                        <ActionMenuItem
                            title={`Acknowledge Alert${
                                renderContext !== 'table' ? 's' : ''
                            }`}
                            hoverIcon={
                                <FontAwesomeIcon icon={solidFaBell} color={link} />
                            }
                            icon={<FontAwesomeIcon icon={faBell} color={secondaryText} />}
                            onSelect={() => {
                                close();
                                onAcknowledgeAlertsClick();
                            }}
                        />
                    )}
                    {canDelete && (
                        <ActionMenuItem
                            title="Delete"
                            hoverIcon={
                                <FontAwesomeIcon
                                    icon={solidFaTrash}
                                    color={deleteButton}
                                />
                            }
                            icon={
                                <FontAwesomeIcon icon={faTrash} color={secondaryText} />
                            }
                            onSelect={() => {
                                close();
                                onDeleteClick();
                            }}
                        />
                    )}
                </ActionMenu>
            )}
        </ActionMenuTableCell>
    );
}
